import React from "react";

export interface TeamClinicianFilterOption {
  id: string;
  name: string;
  isSupervisor: boolean;
  isCurrentUser: boolean;
  count: number;
}

function accessibleCount(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function TeamClinicianFilter({
  value,
  allCount,
  options,
  onChange,
  countLabel,
  countLabelPlural,
  groupLabel,
}: {
  value: string;
  allCount: number;
  options: readonly TeamClinicianFilterOption[];
  onChange: (therapistId: string) => void;
  countLabel: string;
  countLabelPlural: string;
  groupLabel: string;
}) {
  const orderedOptions = [...options].sort((left, right) => {
    const leftOrder = left.isCurrentUser ? 2 : left.isSupervisor ? 1 : 0;
    const rightOrder = right.isCurrentUser ? 2 : right.isSupervisor ? 1 : 0;
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return left.name.localeCompare(right.name);
  });

  return (
    <div
      className="cp-team-clinician-filter"
      data-compact={options.length > 8 || undefined}
    >
      <span className="cp-team-clinician-filter__label">Clinician</span>
      <div
        className="cp-team-clinician-filter__options"
        role="group"
        aria-label={groupLabel}
      >
        <button
          type="button"
          aria-label={`All clinicians: ${accessibleCount(
            allCount,
            countLabel,
            countLabelPlural,
          )}`}
          aria-pressed={!value}
          onClick={() => onChange("")}
        >
          <span>All clinicians</span>
          <span className="cp-team-clinician-filter__count">{allCount}</span>
        </button>
        {orderedOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-label={`${option.name}${
              option.isCurrentUser ? " (You)" : ""
            }: ${accessibleCount(option.count, countLabel, countLabelPlural)}`}
            aria-pressed={value === option.id}
            onClick={() => onChange(option.id)}
          >
            <span>
              {option.name}
              {option.isCurrentUser ? " (You)" : ""}
            </span>
            <span className="cp-team-clinician-filter__count">
              {option.count}
            </span>
          </button>
        ))}
      </div>
      <select
        className="cp-form-input cp-team-clinician-filter__select"
        aria-label="Clinician"
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      >
        <option value="">All clinicians ({allCount})</option>
        {orderedOptions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
            {option.isCurrentUser ? " (You)" : ""} ({option.count})
          </option>
        ))}
      </select>
    </div>
  );
}
